import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Plus, Edit2, Trash2, Save, X } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const EtniasPage = () => {
  const [etnias, setEtnias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    descricao: ''
  });

  useEffect(() => {
    loadEtnias();
  }, []);

  const loadEtnias = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/etnias`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEtnias(response.data);
    } catch (error) {
      console.error('Erro ao carregar etnias:', error);
      toast.error('Erro ao carregar etnias');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      if (editingId) {
        await axios.put(`${API}/api/etnias/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Etnia atualizada com sucesso!');
      } else {
        await axios.post(`${API}/api/etnias`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Etnia criada com sucesso!');
      }
      
      closeModal();
      loadEtnias();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar etnia');
    }
  };

  const handleEdit = (etnia) => {
    setEditingId(etnia.id);
    setFormData({
      nome: etnia.nome,
      descricao: etnia.descricao || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente deletar esta etnia?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/etnias/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Etnia deletada com sucesso!');
      loadEtnias();
    } catch (error) {
      console.error('Erro ao deletar:', error);
      toast.error('Erro ao deletar etnia');
    }
  };

  const openNewModal = () => {
    setFormData({ nome: '', descricao: '' });
    setEditingId(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setFormData({ nome: '', descricao: '' });
    setEditingId(null);
    setShowModal(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Etnias</h1>
          <p className="text-slate-600 mt-1">Gerencie as etnias da empresa</p>
        </div>
        <Button onClick={openNewModal} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Nova Etnia
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Etnias Cadastradas</CardTitle>
          <CardDescription>
            Total: {etnias.length} {etnias.length === 1 ? 'etnia' : 'etnias'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {etnias.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <p>Nenhuma etnia cadastrada.</p>
              <p className="text-sm mt-2">Clique em "Nova Etnia" para começar.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {etnias.map((etnia) => (
                <div
                  key={etnia.id}
                  className="flex items-start justify-between p-4 border rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-800">{etnia.nome}</h3>
                    {etnia.descricao && (
                      <p className="text-sm text-slate-600 mt-1">{etnia.descricao}</p>
                    )}
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(etnia)}
                      className="flex items-center gap-1"
                    >
                      <Edit2 className="h-3 w-3" />
                      Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(etnia.id)}
                      className="flex items-center gap-1"
                    >
                      <Trash2 className="h-3 w-3" />
                      Deletar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="bg-orange-600 text-white p-4 rounded-t-lg">
              <h3 className="text-xl font-bold">
                {editingId ? 'Editar Etnia' : 'Nova Etnia'}
              </h3>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome <span className="text-red-500">*</span></Label>
                <Input
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Ex: Branco, Pardo, Negro, Amarelo, Indígena"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="descricao">Descrição</Label>
                <Textarea
                  id="descricao"
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  placeholder="Descrição opcional da etnia"
                  rows={3}
                />
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeModal}
                  className="flex-1"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1">
                  <Save className="h-4 w-4 mr-2" />
                  {editingId ? 'Atualizar' : 'Salvar'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EtniasPage;
